/**
 * React Query key factory — EPIC 26 SATIS (Enquêtes de satisfaction).
 *
 * Fabrique unique (_INTRO §3.9) : toutes les clés incluent l'`entityId` courant
 * quand la donnée est périmétrée entité. Interdit : clés en dur dans les hooks.
 * Étalée dans le barrel `queryKeys.ts` (point d'import public `@/lib/queryKeys`).
 */

const questionnairesAll = ['satisfaction', 'questionnaires'] as const
const sendingsAll = ['satisfaction', 'sendings'] as const
const statsAll = ['satisfaction', 'stats'] as const

export const satisfactionKeys = {
    satisfaction: {
        questionnaires: {
            all: questionnairesAll,
            list: (filters?: Record<string, unknown>) =>
                [...questionnairesAll, 'list', filters ?? {}] as const,
            // ⚠️ L'ENTITÉ entre dans la clé, comme l'annonce l'en-tête de ce
            // fichier. Ces routes sont résolues par la CHAÎNE d'entités :
            // `read_only`, `is_inherited`, `eligible` et le défaut résolu en
            // dépendent. Sans l'entité, la bascule reservait le cache précédent
            // pendant le refetch — boutons « Ajouter / Modifier » actifs sur une
            // fiche qui allait devenir en lecture seule.
            detail: (entityId: string | null | undefined, id: string) =>
                [...questionnairesAll, 'detail', entityId ?? '', id] as const,
            preview: (
                entityId: string | null | undefined,
                id: string,
                params?: Record<string, unknown>,
            ) => [...questionnairesAll, 'preview', entityId ?? '', id, params ?? {}] as const,
        },
        tagFamilies: (entityId: string | null | undefined, category?: string) =>
            ['satisfaction', 'tag-families', entityId ?? '', category ?? 'all'] as const,
        moveTargets: (entityId: string | null | undefined) =>
            ['satisfaction', 'move-targets', entityId ?? ''] as const,
        reminderConfigs: (entityId: string) =>
            ['satisfaction', 'reminder-configs', entityId] as const,
        productAssignments: (entityId: string | null | undefined, productId: string) =>
            ['satisfaction', 'product-assignments', entityId ?? '', productId] as const,
        // Questions propres à un client — la clé vivait EN DUR dans le hook,
        // hors fabrique et sans entité (interdit par l'en-tête de ce fichier).
        clientQuestions: (entityId: string | null | undefined, clientId: string) =>
            ['satisfaction', 'client-questions', entityId ?? '', clientId] as const,
        resolve: (sessionId: string, category: string) =>
            ['satisfaction', 'resolve', sessionId, category] as const,
        sendings: {
            all: sendingsAll,
            list: (filters?: Record<string, unknown>) =>
                [...sendingsAll, 'list', filters ?? {}] as const,
        },
        stats: {
            product: (id: string) => [...statsAll, 'product', id] as const,
            instructor: (id: string) => [...statsAll, 'instructor', id] as const,
            client: (id: string) => [...statsAll, 'client', id] as const,
            // ⚠️ Apprenant : des SOLLICITATIONS, jamais des agrégats. La clé est
            // rangée ici par voisinage de module, pas parce que la fiche
            // apprenant afficherait une statistique la concernant (SATIS-012 §5.9).
            learner: (id: string) => [...statsAll, 'learner', id] as const,
        },
        results: (filters?: Record<string, unknown>) =>
            ['satisfaction', 'results', filters ?? {}] as const,
        aiSummary: (sessionId: string, category: string) =>
            ['satisfaction', 'ai-summary', sessionId, category] as const,
    },
}
